import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import type { ComponentProps, CSSProperties, FunctionComponent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { motionDuration, motionEase } from '../../theme/motion';

export type TabsProps = {
    readonly children: ReactNode;
    readonly defaultValue?: string;
    readonly onValueChange?: (value: string) => void;
    readonly orientation?: 'horizontal';
    readonly value?: string;
};

export type TabsListProps = ComponentProps<typeof List>;

export type TabsTriggerProps = ComponentProps<typeof Trigger>;

export type TabsContentProps = Omit<ComponentProps<typeof Content>, 'children' | 'value'> & {
    readonly children: ReactNode;
    readonly value: string;
};

export type TabItem = {
    readonly label: ReactNode;
    readonly value: string;
};

export type AnimatedTabs = Omit<TabsProps, 'children'> & {
    readonly children?: ReactNode;
    readonly style?: CSSProperties;
    readonly tabs: readonly TabItem[];
};

const tabsTriggerClasses = clsx(
    'flex items-center justify-center relative z-[2]',
    'text-body font-normal text-text-content-subtle',
    'py-xs px-l rounded-md cursor-pointer select-none',
    'transition-[background-color,color,box-shadow] duration-standard ease-standard',
    'focus-visible:outline-1 focus-visible:outline-focus-ring focus-visible:outline-offset-[1px]',
    'data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
    'data-[state=active]:text-text-content-strong data-[state=active]:z-[2]',
    'hover:data-[state=inactive]:bg-action-secondary-hover hover:data-[state=inactive]:text-text-content-strong',
);

const TabsTriggerComponent: FunctionComponent<ComponentProps<typeof Trigger>> = ({
    className,
    asChild,
    onBlur,
    onKeyDown,
    onPointerDown,
    ...props
}) => {
    const [pointerFocus, setPointerFocus] = useState(false);

    return (
        <Trigger
            asChild={asChild}
            className={clsx(
                !asChild && 'border-0 bg-transparent font-[inherit]',
                tabsTriggerClasses,
                'data-[pointer-focus=true]:outline-none!',
                className,
            )}
            {...props}
            data-pointer-focus={pointerFocus}
            onBlur={(event) => {
                setPointerFocus(false);
                onBlur?.(event);
            }}
            onKeyDown={(event) => {
                setPointerFocus(false);
                onKeyDown?.(event);
            }}
            onPointerDown={(event) => {
                onPointerDown?.(event);
                if (!event.defaultPrevented && event.button === 0) {
                    setPointerFocus(true);
                }
            }}
        />
    );
};

export const Tabs: FunctionComponent<TabsProps> = ({
    children,
    defaultValue,
    onValueChange,
    orientation = 'horizontal',
    value,
    ...props
}) => {
    return (
        <Root
            {...(defaultValue === undefined ? {} : { defaultValue })}
            orientation={orientation}
            {...(value === undefined ? {} : { value })}
            {...(onValueChange === undefined ? {} : { onValueChange })}
            {...props}
            style={{ position: 'relative' }}
        >
            {children}
        </Root>
    );
};

export const TabsList: FunctionComponent<TabsListProps> = ({ children, className, ...props }) => {
    return (
        <List
            className={clsx('relative isolate flex rounded-xl bg-surface-hover p-xs font-sans', className)}
            {...props}
        >
            {children}
        </List>
    );
};

export const TabsTrigger: FunctionComponent<TabsTriggerProps> = ({ children, disabled, value, ...props }) => {
    return (
        <TabsTriggerComponent disabled={disabled} value={value} {...props}>
            {children}
        </TabsTriggerComponent>
    );
};

export const TabsContent: FunctionComponent<TabsContentProps> = ({ children, className, value, ...props }) => {
    return (
        <Content
            className={clsx(
                'text-text-secondary text-body-lg outline-none p-m',
                'focus-visible:rounded-xs focus-visible:outline-2 focus-visible:outline-border-control-focus focus-visible:outline-offset-2',
                'data-[state=inactive]:hidden',
                // The newly selected panel fades in instead of cutting. Radix skips it
                // on first render.
                'data-[state=active]:animate-panel-in motion-reduce:animate-none!',
                className,
            )}
            value={value}
            {...props}
        >
            {children}
        </Content>
    );
};

export const AnimatedTabs: FunctionComponent<AnimatedTabs> = ({
    children,
    defaultValue,
    onValueChange,
    orientation = 'horizontal',
    tabs,
    value,
    ...props
}) => {
    const [activeTab, setActiveTab] = useState(value ?? defaultValue ?? tabs[0]?.value ?? '');
    const layoutIdRef = useRef<string>(Math.random().toString(36).substring(2, 15));
    useEffect(() => {
        if (value !== undefined && value !== activeTab) {
            setActiveTab(value);
        }
    }, [value, activeTab]);

    const handleValueChange = (newValue: string): void => {
        setActiveTab(newValue);
        onValueChange?.(newValue);
    };

    return (
        <Tabs {...props} orientation={orientation} value={activeTab} onValueChange={handleValueChange}>
            <TabsList>
                {tabs.map((tab) => (
                    <TabsTriggerComponent key={tab.value} value={tab.value}>
                        {tab.label}
                        {activeTab === tab.value && (
                            <motion.span
                                animate={{}}
                                className="absolute inset-0 bg-surface-raised rounded-md shadow-sm -z-[1]"
                                initial={false}
                                layoutId={layoutIdRef.current}
                                tabIndex={-1}
                                transition={{
                                    duration: motionDuration.standard,
                                    ease: motionEase.standard,
                                }}
                            />
                        )}
                    </TabsTriggerComponent>
                ))}
            </TabsList>
            {children}
        </Tabs>
    );
};

/** Animated tabs over the panel of the active tab, as a tile shows its sections. */
export const TabbedPanel: FunctionComponent<{
    readonly onValueChange: (value: string) => void;
    readonly panel: ReactNode;
    readonly panelClassName?: string | undefined;
    readonly tabs: readonly TabItem[];
    readonly testId?: string | undefined;
    readonly value: string;
}> = ({ onValueChange, panel, panelClassName, tabs, testId, value }) => (
    <div className="flex flex-col gap-xxl" data-testid={testId}>
        <div className="flex">
            <AnimatedTabs tabs={tabs} value={value} onValueChange={onValueChange} />
        </div>
        <div className={panelClassName}>{panel}</div>
    </div>
);
